import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFlags } from "@/lib/flags";
import { useAssistant } from "@/lib/assistant";
import { useAssistantShown } from "@/components/AssistantButton";
import { cn } from "@/lib/utils";
import { APP_NAME, ENGAGEMENT_LABELS } from "@/config";
import { KIND_LABELS } from "@/lib/workKinds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Segmented, Loading, Avatar } from "@/components/common";
import ConfirmDestructive from "@/components/ConfirmDestructive";
import {
  TYPE_LABEL, periodLabel, cardTitle, cardStatus, costLine, credits, monthChoices, monthPeriod, weekPeriod, toggleBlock,
  webSources, address, importType, readingLine, dollars, dayLabel, fitLine, createdLine, hireLink,
} from "@/lib/documents";
import {
  MessageCircleQuestion, Lock, PenLine, X, ArrowLeft, ArrowUp, ArrowUpRight, Activity, Clock, FileText, Mail,
  LoaderCircle, CircleCheck, TriangleAlert, Trash2, Send, MessageSquare, UserPlus, Wallet, Globe, Paperclip, Target, Info,
} from "lucide-react";

// design_handoff_founder_assistant screens 1 and 2 (Assistant.dc.html): the founder's assistant, a 400px drawer beside
// any founder page (the page narrows, so they keep working), full screen on a phone. It answers from what the founder
// can already see (backend/assistant.py), never from session conversations, and changes nothing until they accept a
// card. Only founders see it, each their own conversation. It searches the web while the company's web search is on, and
// reads the files the founder imports (part 3b): facts name their page, web page or file. It offers to create objectives
// (part 5): a team member is assigned at once, an independent professional through the hiring form, filled in.

const STARTERS = [
  [Activity, "What happened this week?"],
  [Clock, "What’s waiting for me?"],
  [FileText, "Prepare the monthly money sheet"],
  [Mail, "Prepare the investor update"],
];
const UPPER = "text-xs uppercase tracking-[0.09em] text-muted-foreground";
const ROW = "grid grid-cols-[96px_minmax(0,1fr)] gap-2 px-3.5 py-2 border-t";
const KEY = "text-sm text-muted-foreground";
const PHONE = "(max-width: 767px)";
const isPhone = () => !!window.matchMedia?.(PHONE).matches;
const zone = () => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; } catch { return "UTC"; } };
const capital = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_BYTES = 10 * 1024 * 1024;  // backend assistant_files.MAX_BYTES
const CHIP = "inline-flex items-center gap-1 h-5 px-1.5 border rounded-[4px] text-xs leading-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring";

// the design's crossed globe: web search off
function GlobeOff({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
      className={className} aria-hidden="true"><circle cx="12" cy="12" r="10" /><path d="M2 12h20" /><path d="m3 3 18 18" /></svg>
  );
}

function Cost({ usd }) {
  const line = costLine(usd);
  return line ? <span className="text-xs text-muted-foreground tabular-nums" data-testid="assistant-cost">{line}</span> : null;
}

// a fact's source: the Crewbench page it comes from (a link), a web page (in the info colour, opening it in a new
// tab), or a file the founder imported
function SourceChip({ fact, onLeave }) {
  if (fact.source === "web") {
    return (
      <a href={fact.url} target="_blank" rel="noopener noreferrer" aria-label={`From the web: ${fact.domain}`}
        className={cn(CHIP, "border-gr-success text-gr-success hover:opacity-80")} data-testid="assistant-web-chip">
        <Globe className="w-3 h-3" aria-hidden="true" />{fact.domain}
      </a>
    );
  }
  if (fact.source === "file") {
    return (
      <span aria-label={`From your file: ${fact.name}`} className={cn(CHIP, "border-gr-hairline text-muted-foreground max-w-full")}
        data-testid="assistant-file-chip">
        <FileText className="w-3 h-3 shrink-0" aria-hidden="true" /><span className="truncate">{fact.name}</span>
      </span>
    );
  }
  if (!fact.link) return null;
  return (
    <Link to={fact.link} onClick={onLeave} aria-label={`Source: ${fact.name}`}
      className={cn(CHIP, "border-gr-hairline text-muted-foreground hover:text-foreground")}>
      {fact.name}<ArrowUpRight className="w-3 h-3 rtl:-scale-x-100" aria-hidden="true" />
    </Link>
  );
}

// FROM THE WEB: each page an answer used, its title over its address
function FromTheWeb({ facts }) {
  const pages = webSources(facts);
  if (!pages.length) return null;
  return (
    <div className="border border-gr-hairline rounded-[8px] px-3 py-2.5 flex flex-col gap-2" data-testid="assistant-from-web">
      <span className="flex items-center gap-1.5 text-xs uppercase tracking-[0.09em] text-gr-success">
        <Globe className="w-3 h-3" aria-hidden="true" />From the web
      </span>
      {pages.map((p) => (
        <a key={p.url} href={p.url} target="_blank" rel="noopener noreferrer" className="flex flex-col min-w-0 hover:opacity-80">
          <span className="text-gr-success underline underline-offset-2">{p.title}</span>
          <span className="text-sm text-muted-foreground break-all">{address(p.url)}</span>
        </a>
      ))}
    </div>
  );
}

function Answer({ m, onLeave }) {
  return (
    <div className="flex flex-col gap-2" data-testid="assistant-answer">
      {m.intro && <p className="m-0">{m.intro}</p>}
      {m.facts?.length > 0 && (
        <ul className="m-0 p-0 list-none flex flex-col gap-1.5">
          {m.facts.map((f, i) => (
            <li key={i} className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="w-1 h-1 rounded-full bg-muted-foreground shrink-0" aria-hidden="true" />
              <span>{f.text}</span><SourceChip fact={f} onLeave={onLeave} />
            </li>
          ))}
        </ul>
      )}
      <FromTheWeb facts={m.facts} />
      {m.outro && <p className="m-0">{m.outro}</p>}
      <Cost usd={m.cost_usd} />
    </div>
  );
}

// what it never does in v1, and who can help (backend assistant.refusal)
function Refusal({ m, onGo, onLeave }) {
  return (
    <div className="flex flex-col gap-2.5" data-testid="assistant-refusal">
      <p className="m-0">{m.text}</p>
      {m.link && (  // web search off: where to turn it on
        <Link to={m.link.to} onClick={onLeave} data-testid="assistant-web-switch"
          className="self-start font-semibold underline underline-offset-2 hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">
          {m.link.label}
        </Link>
      )}
      {m.buttons?.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {m.buttons.map((b) => (
            <Button key={b.kind} variant="outline" data-testid={`assistant-refusal-${b.kind}`}
              onClick={() => onGo(b.kind === "message" ? `/founder/messages?to=${encodeURIComponent(b.person_id)}` : "/founder/team?invite=1")}>
              {b.kind === "message" ? <MessageSquare /> : <UserPlus />}{b.label}
            </Button>
          ))}
        </div>
      )}
      <Cost usd={m.cost_usd} />
    </div>
  );
}

// the period, once Change opened the fields: a month, or this week or the last full one
function PeriodPicker({ id, period, onChange }) {
  if (period.kind === "week") {
    const last = weekPeriod(true).start === period.start;
    return (
      <Segmented label="Period" value={last ? "last" : "this"} className="justify-self-start" testid="card-week"
        options={[["last", "Last week"], ["this", "This week"]]} onChange={(v) => onChange(weekPeriod(v === "last"))} />
    );
  }
  return (
    <select id={id} value={period.start.slice(0, 7)} onChange={(e) => onChange(monthPeriod(e.target.value))}
      className="justify-self-start h-7 px-2 border border-gr-control-border rounded-md bg-background" data-testid="card-month">
      {monthChoices().map(([v, label]) => <option key={v} value={v}>{label}</option>)}
    </select>
  );
}

// PREPARE A DOCUMENT: what the assistant offers to prepare, for the founder to check, change and accept
function PrepareCard({ m, onAccept, pending }) {
  const [blocks, setBlocks] = useState(m.blocks || []);
  const [format, setFormat] = useState(m.format);
  const [period, setPeriod] = useState(m.period);
  const [note, setNote] = useState(m.note || "");
  const [recipients, setRecipients] = useState(m.recipients || []);
  const [address, setAddress] = useState("");
  const [editing, setEditing] = useState(false);
  const investor = m.type === "investor_update";
  const pdfOnly = ["money_sheet", "objective_brief"].includes(m.type);
  const chosen = blocks.filter((b) => b.on && b.allowed && !b.needs).map((b) => b.key);
  const ready = !m.blocks || chosen.length > 0;
  const prepare = () => ready && onAccept(m.id, {
    format, note, recipients, ...(period ? { period } : {}), ...(m.blocks ? { blocks: chosen } : {}),
  });
  const add = (e) => {
    e.preventDefault();
    const email = address.trim();
    if (!EMAIL.test(email)) { toast.error("Type an email address."); return; }
    if (!recipients.some((r) => r.email.toLowerCase() === email.toLowerCase())) {
      setRecipients([...recipients, { name: "", email, kind: "address" }]);
    }
    setAddress("");
  };
  return (
    <section aria-label="Prepare a document" className="bg-card rounded-[10px] flex flex-col" data-testid="assistant-card">
      <div className="flex items-center gap-2 px-3.5 pt-3 pb-2.5">
        <FileText className="w-4 h-4" aria-hidden="true" /><span className={UPPER}>Prepare a document</span>
      </div>
      <div className={ROW}><span className={KEY}>Type</span><span className="font-semibold">{TYPE_LABEL[m.type]}</span></div>
      {m.type === "objective_brief" ? (
        <div className={ROW}><span className={KEY}>Objective</span><span>{m.objective_title}</span></div>
      ) : period && (
        <div className={cn(ROW, "items-center")}>
          {editing ? <label htmlFor={`${m.id}-period`} className={KEY}>Period</label> : <span className={KEY}>Period</span>}
          {editing ? <PeriodPicker id={`${m.id}-period`} period={period} onChange={setPeriod} />
            : <span data-testid="card-period">{periodLabel(period)}</span>}
        </div>
      )}
      {m.blocks && (
        <div className={ROW} role="group" aria-labelledby={`${m.id}-blocks`}>
          <span id={`${m.id}-blocks`} className={KEY}>What goes in</span>
          <span className="flex flex-col gap-0.5">
            {blocks.map((b) => {
              const off = !b.allowed || b.needs;
              return (
                <label key={b.key} className={cn("flex items-center gap-2 min-h-6", (!b.on || off) && "text-muted-foreground",
                  off ? "cursor-not-allowed" : "cursor-pointer")}
                  title={!b.allowed ? "Off in your investor sharing settings" : b.needs ? "Needs Money per objective" : undefined}>
                  <input type="checkbox" checked={b.on && !off} disabled={off} data-testid={`card-block-${b.key}`}
                    onChange={(e) => setBlocks(toggleBlock(blocks, b.key, e.target.checked))}
                    className="w-3.5 h-3.5 m-0 accent-[var(--gr-ink)]" />{b.label}
                </label>
              );
            })}
          </span>
        </div>
      )}
      {investor && (editing || note) && (
        <div className={ROW}>
          {editing ? <label htmlFor={`${m.id}-note`} className={KEY}>Your note</label> : <span className={KEY}>Your note</span>}
          {editing ? (
            <textarea id={`${m.id}-note`} rows={3} maxLength={2000} value={note} onChange={(e) => setNote(e.target.value)}
              className="w-full px-2 py-1.5 border border-gr-control-border rounded-md bg-background resize-y" data-testid="card-note" />
          ) : <span className="whitespace-pre-wrap break-words line-clamp-3">{note}</span>}
        </div>
      )}
      <div className={cn(ROW, "items-center")}>
        <span className={KEY}>Format</span>
        {pdfOnly ? <span>PDF only</span> : (
          <Segmented label="Format" value={format} onChange={setFormat} className="justify-self-start" testid="card-format"
            options={[["email_pdf", "Email and PDF"], ["pdf", "PDF only"]]} />
        )}
      </div>
      <div className={cn(ROW, "items-center")}>
        <label htmlFor={`${m.id}-lang`} className={KEY}>Language</label>
        <span className="flex items-center gap-2 flex-wrap">
          <select id={`${m.id}-lang`} defaultValue="en" className="h-7 px-2 border border-gr-control-border rounded-md bg-background">
            <option value="en">English</option>
            <option value="de" disabled>Deutsch (later)</option>
            <option value="ar" disabled>العربية (later)</option>
          </select>
          <span className="text-xs text-muted-foreground">Deutsch and العربية later</span>
        </span>
      </div>
      {(investor || recipients.length > 0) && (
        <div className={cn(ROW, "items-center")}>
          <span className={KEY}>Recipients</span>
          <span className="flex flex-col gap-2 min-w-0">
            <span className="flex gap-1.5 flex-wrap" data-testid="card-recipients">
              {recipients.length ? recipients.map((r) => (
                <span key={r.email} className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-gr-surface-2 max-w-full">
                  <span className="truncate">{r.name || r.email}</span>
                  {editing && (
                    <button type="button" aria-label={`Remove ${r.name || r.email}`} onClick={() => setRecipients(recipients.filter((x) => x !== r))}
                      className="-me-1 w-5 h-5 grid place-items-center rounded-full hover:bg-gr-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">
                      <X className="w-3 h-3" aria-hidden="true" />
                    </button>
                  )}
                </span>
              )) : <span className="text-muted-foreground">{investor ? "No investors yet." : "Nobody yet."}</span>}
            </span>
            {editing && (
              <form onSubmit={add} className="flex gap-1.5">
                <Input type="email" aria-label="Add an email address" placeholder="name@company.com" value={address}
                  onChange={(e) => setAddress(e.target.value)} className="min-w-0" data-testid="card-address" />
                <Button type="submit" variant="outline">Add</Button>
              </form>
            )}
          </span>
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap px-3.5 py-3 border-t">
        <span className="flex-1 min-w-[120px] text-sm text-muted-foreground">{capital(credits(m.estimate_usd || 0))}</span>
        {!editing && <Button variant="outline" onClick={() => setEditing(true)} data-testid="card-change">Change</Button>}
        <Button onClick={prepare} disabled={pending} aria-disabled={!ready} title={ready ? undefined : "Tick what goes in."}
          data-testid="card-prepare">Prepare</Button>
      </div>
    </section>
  );
}

const FIELD = "w-full px-2 py-1.5 border border-gr-control-border rounded-md bg-background";
const SELECT = "h-7 max-w-full px-2 border border-gr-control-border rounded-md bg-background";

// who works on it: avatar, name, and "Independent professional · Writing & translation"
function Person({ p }) {
  return (
    <span className="flex items-center gap-2 min-w-0">
      <Avatar name={p.name} size={28} />
      <span className="flex flex-col min-w-0">
        <span className="font-semibold truncate">{p.name}</span>
        <span className="text-sm text-muted-foreground truncate">
          {[ENGAGEMENT_LABELS[p.engagement === "employee" ? "employee" : "independent"], KIND_LABELS[p.work_kind]].filter(Boolean).join(" · ")}
        </span>
      </span>
    </span>
  );
}

// Create an objective (design screen 2): nothing is created until the founder accepts. Change opens the fields; a field
// sent is theirs (an empty project, date or person clears it)
function ObjectiveCard({ m, onAccept, pending }) {
  const [v, setV] = useState({
    project_id: m.project?.id || "", title: m.title || "", description: m.description || "",
    success_criteria: m.success_criteria || "", budget: m.budget_usd ? String(m.budget_usd) : "", due_date: m.due_date || "",
    person_id: m.person?.id || "",
  });
  const [editing, setEditing] = useState(false);
  const set = (patch) => setV({ ...v, ...patch });
  const { data: projects } = useQuery({ queryKey: ["projects"], enabled: editing, queryFn: async () => (await api.get("/projects")).data });
  const { data: team } = useQuery({ queryKey: ["team"], enabled: editing, queryFn: async () => (await api.get("/team")).data });
  const projectList = projects?.projects || (m.project ? [m.project] : []);
  const people = (team?.members || []).filter((p) => p.role === "coder");
  const project = projectList.find((p) => p.id === v.project_id) || null;
  const person = people.find((p) => p.id === v.person_id) || (m.person?.id === v.person_id ? m.person : null);
  const budget = v.budget.trim() === "" ? 0 : Number(v.budget);
  const ready = v.title.trim() && Number.isFinite(budget) && budget >= 0;
  const first = (person?.name || "").split(" ")[0];
  const create = () => ready && onAccept(m.id, !editing ? {} : {
    project_id: v.project_id || null, title: v.title.trim(), description: v.description.trim(),
    success_criteria: v.success_criteria.trim(), budget_usd: budget, due_date: v.due_date || null, person_id: v.person_id || null,
  });
  const id = (k) => `${m.id}-${k}`;
  const label = (k, text) => (editing ? <label htmlFor={id(k)} className={KEY}>{text}</label> : <span className={KEY}>{text}</span>);
  const text = (k, rows) => (
    <textarea id={id(k)} rows={rows} maxLength={5000} value={v[k]} onChange={(e) => set({ [k]: e.target.value })}
      className={cn(FIELD, "resize-y")} data-testid={`card-${k}`} />
  );
  return (
    <section aria-label="Create an objective" className="bg-card rounded-[10px] flex flex-col" data-testid="assistant-objective-card">
      <div className="flex items-center gap-2 px-3.5 pt-3 pb-2.5">
        <Target className="w-4 h-4" aria-hidden="true" /><span className={UPPER}>Create an objective</span>
      </div>
      <div className={cn(ROW, "items-center")}>
        {label("project", "Project")}
        {editing ? (
          <select id={id("project")} value={v.project_id} onChange={(e) => set({ project_id: e.target.value })} className={SELECT}
            data-testid="card-project">
            <option value="">No project</option>
            {projectList.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        ) : <span>{project?.name || "No project"}</span>}
      </div>
      <div className={cn(ROW, editing && "items-center")}>
        {label("title", "Title")}
        {editing ? <Input id={id("title")} value={v.title} maxLength={200} onChange={(e) => set({ title: e.target.value })} data-testid="card-title" />
          : <span className="font-semibold break-words">{v.title}</span>}
      </div>
      {(editing || v.description) && (
        <div className={ROW}>{label("description", "Description")}
          {editing ? text("description", 3) : <span className="break-words whitespace-pre-wrap">{v.description}</span>}</div>
      )}
      {(editing || v.success_criteria) && (
        <div className={ROW}>{label("success_criteria", "Success")}
          {editing ? text("success_criteria", 2) : <span className="break-words whitespace-pre-wrap">{v.success_criteria}</span>}</div>
      )}
      <div className={cn(ROW, "items-center")}>
        {label("budget", "AI budget")}
        {editing ? (
          <span className="relative max-w-[140px]">
            <span className="absolute inset-y-0 start-2.5 flex items-center text-muted-foreground pointer-events-none" aria-hidden="true">$</span>
            <Input id={id("budget")} inputMode="decimal" className="ps-6 tabular-nums" value={v.budget} placeholder="0"
              onChange={(e) => set({ budget: e.target.value })} data-testid="card-budget" />
          </span>
        ) : <span className="tabular-nums">{budget ? dollars(budget) : "No AI budget"}</span>}
      </div>
      <div className={cn(ROW, "items-center")}>
        {label("due", "Due")}
        {editing ? (
          <Input id={id("due")} type="date" value={v.due_date} onChange={(e) => set({ due_date: e.target.value })}
            className="max-w-[170px]" data-testid="card-due" />
        ) : <span>{v.due_date ? dayLabel(v.due_date) : "No date"}</span>}
      </div>
      <div className={cn(ROW, "items-center")}>
        {label("person", "Works on it")}
        {editing ? (
          <select id={id("person")} value={v.person_id} onChange={(e) => set({ person_id: e.target.value })} className={SELECT}
            data-testid="card-person">
            <option value="">Nobody yet</option>
            {(people.length ? people : m.person ? [m.person] : []).map((p) => <option key={p.id} value={p.id}>{p.name || p.email}</option>)}
          </select>
        ) : person ? <Person p={person} /> : <span className="text-muted-foreground">Nobody yet</span>}
      </div>
      {person && (person.engagement === "employee" ? (
        <p className="m-0 px-3.5 py-2 border-t text-sm text-muted-foreground">Team member: assigned as soon as you create it.</p>
      ) : (
        <p className="m-0 mx-3.5 mb-1 mt-2 px-3 py-2.5 rounded-lg border border-gr-success text-sm flex gap-2"
          data-testid="card-hiring">
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden="true" />
          {first} joins through hiring terms. The form opens filled in, for you to check and send.
        </p>
      ))}
      <div className="flex items-center gap-2 flex-wrap px-3.5 py-3 border-t">
        <span className="flex-1 min-w-[120px] text-sm text-muted-foreground">{fitLine(project, m.fits?.[v.project_id])}</span>
        {!editing && <Button variant="outline" onClick={() => setEditing(true)} data-testid="card-change">Change</Button>}
        <Button onClick={create} disabled={pending} aria-disabled={!ready} title={ready ? undefined : "Add a title and a budget in dollars."}
          data-testid="card-create">Create objective</Button>
      </div>
    </section>
  );
}

// a created objective, collapsed: its project, budget and date, and the hiring form while an independent professional
// still has to agree
function ObjectiveDone({ m, onLeave }) {
  const LINK = "font-semibold underline underline-offset-2 hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring";
  return (
    <section aria-label={`Created ${m.title}`} className="bg-card rounded-[10px] px-3.5 py-3 flex items-start gap-2.5"
      data-testid="assistant-objective-done">
      <CircleCheck className="w-4 h-4 mt-0.5 shrink-0 text-gr-success" aria-hidden="true" />
      <span className="flex-1 min-w-0 flex flex-col gap-0.5">
        <span className="font-semibold"><span className="text-gr-success">Created</span> · {m.title}</span>
        <span className="text-sm text-muted-foreground">{createdLine(m)}</span>
        <span className="flex gap-x-3 gap-y-1 flex-wrap mt-1">
          <Link to={`/founder/objectives/${m.objective_id}`} onClick={onLeave} className={LINK}>Open objective</Link>
          {m.hire && m.person && <Link to={hireLink(m)} onClick={onLeave} className={LINK} data-testid="card-hire-link">Fill in the hiring form</Link>}
        </span>
      </span>
    </section>
  );
}

const STATUS_ICON = {
  preparing: [LoaderCircle, "text-gr-ink-secondary motion-safe:animate-spin"],
  ready: [CircleCheck, "text-gr-success"],
  sent: [Send, "text-gr-ink-secondary"],
  nocheck: [TriangleAlert, "text-gr-danger"],
  failed: [TriangleAlert, "text-gr-danger"],
  deleted: [Trash2, "text-muted-foreground"],
};
const TONE = { info: "text-gr-success", danger: "text-gr-danger", muted: "text-muted-foreground", plain: "" };

// an accepted card, collapsed to its document: Preparing…, then Ready (or what went wrong), with its link
function AcceptedCard({ m, onLeave }) {
  const { data: doc, error } = useQuery({
    queryKey: ["document", m.document_id], enabled: !!m.document_id,
    queryFn: async () => (await api.get(`/documents/${m.document_id}`)).data.document,
    retry: (n, e) => e?.response?.status !== 404 && n < 2,
    refetchInterval: (q) => (q.state.data?.status === "preparing" ? 5000 : false),
  });
  const gone = error?.response?.status === 404;
  const shown = gone ? null : doc || { ...m, status: "preparing" };
  const s = cardStatus(shown);
  const [Icon, tone] = STATUS_ICON[gone ? "deleted" : shown.status] || [CircleCheck, ""];
  const title = cardTitle(doc || m);
  return (
    <section aria-label={`${s.word} ${title}`} className="bg-card rounded-[10px] px-3.5 py-3 flex items-start gap-2.5"
      data-testid="assistant-card-done">
      <Icon className={cn("w-4 h-4 mt-0.5 shrink-0", tone)} aria-hidden="true" />
      <span className="flex-1 min-w-0 flex flex-col gap-0.5">
        <span className="font-semibold">
          {shown?.status === "preparing" ? `Preparing… ${title}` : <><span className={TONE[s.tone]}>{s.word}</span> · {title}</>}
        </span>
        {s.line && <span className="text-sm text-muted-foreground">{s.line}</span>}
        {s.link && (
          <Link to={s.link === "documents" ? "/founder/documents" : `/founder/documents/${m.document_id}`} onClick={onLeave}
            className="self-start mt-1 font-semibold underline underline-offset-2 hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">
            {s.link === "documents" ? "Open Documents" : "Preview"}
          </Link>
        )}
      </span>
    </section>
  );
}

// the input's place when the assistant can't answer: no credits (the Store), or not available right now
function Unavailable({ data, onLeave }) {
  const flags = useFlags();
  return (
    <div className="shrink-0 px-4 pt-3 pb-4 border-t">
      <div className="bg-card rounded-[10px] p-3.5 flex flex-col gap-3" data-testid="assistant-unavailable">
        <span className="flex gap-2.5">
          {data.no_credits ? <Wallet className="w-4 h-4 mt-0.5 shrink-0 text-gr-warning" aria-hidden="true" />
            : <TriangleAlert className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" aria-hidden="true" />}
          <span>{data.reason}</span>
        </span>
        {data.no_credits && flags.store && (
          <Button asChild className="self-start"><Link to="/founder/store#topup" onClick={onLeave}>Store</Link></Button>
        )}
      </div>
    </div>
  );
}

function Drawer() {
  const a = useAssistant();
  const key = ["assistant", useAuth().user.id];  // each founder's own conversation
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data } = useQuery({ queryKey: key, queryFn: async () => (await api.get("/assistant")).data });
  const [asking, setAsking] = useState(null);  // the founder's text on its way
  const list = useRef(null);
  const field = useRef(null);
  // on a phone the drawer covers the page: going to a page closes it
  const leave = () => { if (isPhone()) a.setOpen(false); };
  const go = (to) => { leave(); navigate(to); };

  // files imported with the message being written: read in the background, followed until they're read
  const picker = useRef(null);
  const reading = a.files.filter((f) => f.status === "reading");
  const blocked = a.files.some((f) => f.status !== "ready");
  const importing = useMutation({
    mutationFn: async (file) => {
      const data = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result).split(",")[1] || "");
        r.onerror = () => reject(new Error("This file couldn't be opened."));
        r.readAsDataURL(file);
      });
      return (await api.post("/assistant/files", { name: file.name, media_type: importType(file.name, file.type), data })).data.file;
    },
    onSuccess: (f) => a.setFiles((was) => [...was, f]),
    onError: (e) => toast.error(apiErr(e)),
  });
  const pick = (list) => {
    for (const file of [...list].slice(0, 5 - a.files.length)) {
      if (!importType(file.name, file.type)) toast.error("Import a PDF, a CSV or an image.");
      else if (file.size > MAX_BYTES) toast.error("Import a file of 10 MB or less.");
      else importing.mutate(file);
    }
  };
  useEffect(() => {
    if (!reading.length) return undefined;
    const timer = setInterval(async () => {
      for (const f of reading) {
        try {
          const now = (await api.get(`/assistant/files/${f.id}`)).data.file;
          a.setFiles((was) => was.map((x) => (x.id === now.id ? now : x)));
        } catch { /* asked again on the next beat */ }
      }
    }, 1500);
    return () => clearInterval(timer);
  }, [reading.map((f) => f.id).join()]);  // eslint-disable-line react-hooks/exhaustive-deps

  const send = useMutation({
    mutationFn: async ({ text, files }) => (await api.post("/assistant/messages", { text, tz: zone(), files })).data,
    onMutate: ({ text }) => { setAsking(text); a.setDraft(""); },
    onSuccess: (d) => { qc.setQueryData(key, (old) => ({ ...d, messages: [...(old?.messages || []), ...d.messages] })); a.setFiles([]); },
    onError: (e, { text }) => {
      a.setDraft((d) => d || text);
      if (e?.response?.status !== 402) toast.error(apiErr(e));
      qc.invalidateQueries({ queryKey: key });  // no credits: the input gives way to the Store card
    },
    onSettled: () => setAsking(null),
  });
  const fresh = useMutation({
    mutationFn: async () => (await api.post("/assistant/new")).data,
    onSuccess: (d) => qc.setQueryData(key, d),
    onError: (e) => toast.error(apiErr(e)),
  });
  // Delete conversations: this one and the earlier ones New conversation put away, for good, with the password
  const [deleting, setDeleting] = useState(false);
  const remove = useMutation({
    mutationFn: async (password) => (await api.delete("/assistant", { data: { password } })).data,
    onSuccess: (d) => { qc.setQueryData(key, d); setDeleting(false); toast.success("Conversations deleted."); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const accept = useMutation({
    mutationFn: async ({ id, body }) => (await api.post(`/assistant/cards/${id}/accept`, body)).data,
    onSuccess: (d) => {
      qc.setQueryData(key, (old) => old && ({ ...old, messages: old.messages.map((m) => (m.id === d.message.id ? d.message : m)) }));
      if (d.document) {
        qc.setQueryData(["document", d.document.id], d.document);
        qc.invalidateQueries({ queryKey: ["documents"] });
      }
      if (d.objective) ["objectives", "team", "overview"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  const ask = (text) => {
    const t = text.trim();
    if (!t || send.isPending || blocked) return;
    if (data?.available) send.mutate({ text: t, files: a.files.map((f) => f.id) }); else a.setDraft(t);
  };

  const messages = data?.messages || [];
  const count = messages.length + (asking ? 1 : 0);
  useEffect(() => { const el = list.current; if (el) el.scrollTop = el.scrollHeight; }, [count]);
  useEffect(() => { if (!isPhone()) field.current?.focus(); }, []);

  const empty = data && !messages.length && !asking;
  return (
    <aside aria-label="Assistant" data-testid="assistant"
      onKeyDown={(e) => {  // not from a select, nor from a dialog's portal (Delete conversations)
        if (e.key === "Escape" && e.target.tagName !== "SELECT" && e.currentTarget.contains(e.target)) a.setOpen(false);
      }}
      className="fixed inset-0 z-50 flex flex-col bg-background md:sticky md:top-0 md:inset-auto md:z-auto md:w-[400px] md:shrink-0 md:h-screen md:h-dvh md:border-s">
      <header className="h-14 shrink-0 flex items-center gap-1.5 ps-4 pe-2.5 border-b">
        <Button variant="ghost" size="icon" className="md:hidden -ms-2.5 w-11 h-11" aria-label="Back" onClick={() => a.setOpen(false)}>
          <ArrowLeft className="rtl:-scale-x-100" />
        </Button>
        <div className="flex-1 min-w-0 flex flex-col">
          <h2 className="m-0 font-display text-[15px] leading-5 font-semibold tracking-[0.02em]">Assistant</h2>
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            <Lock className="w-[11px] h-[11px]" aria-hidden="true" />Only founders see this
          </span>
        </div>
        <Button variant="ghost" size="icon" aria-label="New conversation" title="New conversation" data-testid="assistant-new"
          disabled={!messages.length || fresh.isPending || send.isPending} onClick={() => fresh.mutate()}><PenLine /></Button>
        <Button variant="ghost" size="icon" aria-label="Delete conversations" title="Delete conversations" data-testid="assistant-delete"
          disabled={!(messages.length || data?.earlier) || send.isPending} onClick={() => setDeleting(true)}><Trash2 /></Button>
        <Button variant="ghost" size="icon" className="hidden md:inline-flex" aria-label="Close the assistant" title="Close the assistant"
          onClick={() => a.setOpen(false)} data-testid="assistant-close"><X /></Button>
      </header>

      <div ref={list} className="flex-1 min-h-0 overflow-y-auto p-4 flex flex-col gap-[18px]" aria-live="polite" data-testid="assistant-messages">
        {!data && <Loading />}
        {empty && (
          <div className="my-auto flex flex-col gap-5 py-2" data-testid="assistant-empty">
            <div className="flex flex-col gap-2">
              <span className="w-9 h-9 rounded-[10px] bg-card grid place-items-center">
                <MessageCircleQuestion className="w-[18px] h-[18px]" aria-hidden="true" />
              </span>
              <p className="m-0 font-display text-[18px] leading-6 font-semibold tracking-[0.01em]">
                Ask about your work, money and reliability, or prepare a document.
              </p>
              <p className="m-0 text-sm text-muted-foreground">
                It reads what you can already see in {APP_NAME}. It never reads session conversations, and it changes
                nothing until you accept a card.
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <span className={UPPER}>Start with</span>
              {STARTERS.map(([Icon, text]) => (
                <button key={text} type="button" onClick={() => ask(text)} data-testid="assistant-starter"
                  className="flex items-center gap-2.5 min-h-10 px-3 py-2 rounded-[8px] bg-card text-start transition-colors hover:bg-gr-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">
                  <Icon className="w-4 h-4 shrink-0" aria-hidden="true" />{text}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m) => {
          if (m.kind === "founder") return <Bubble key={m.id} text={m.text} files={m.files} />;
          if (m.kind === "answer") return <Answer key={m.id} m={m} onLeave={leave} />;
          if (m.kind === "refusal") return <Refusal key={m.id} m={m} onGo={go} onLeave={leave} />;
          if (m.kind === "card" && m.card === "create_objective") {
            return m.state === "open"
              ? <ObjectiveCard key={m.id} m={m} pending={accept.isPending} onAccept={(id, body) => accept.mutate({ id, body })} />
              : <ObjectiveDone key={m.id} m={m} onLeave={leave} />;
          }
          if (m.kind === "card") {
            return m.state === "open"
              ? <PrepareCard key={m.id} m={m} pending={accept.isPending} onAccept={(id, body) => accept.mutate({ id, body })} />
              : <AcceptedCard key={m.id} m={m} onLeave={leave} />;
          }
          return (
            <div key={m.id} className="flex flex-col gap-2" data-testid="assistant-error">
              <p className="m-0 flex gap-2 text-muted-foreground">
                <TriangleAlert className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />{m.text}
              </p>
              <Cost usd={m.cost_usd} />
            </div>
          );
        })}
        {asking && (
          <>
            <Bubble text={asking} />
            <span className="flex items-center text-muted-foreground" role="status">
              <LoaderCircle className="w-4 h-4 motion-safe:animate-spin" aria-hidden="true" />
              <span className="sr-only">The assistant is answering</span>
            </span>
          </>
        )}
      </div>

      <ConfirmDestructive open={deleting} onOpenChange={setDeleting} title="Delete your conversations?" action="Delete"
        pending={remove.isPending} onConfirm={(password) => remove.mutate(password)}>
        <p className="m-0 [text-wrap:pretty]">This conversation and the earlier ones you started over from are deleted for
          good. The documents it prepared stay in Documents.</p>
      </ConfirmDestructive>
      {data && !data.available ? <Unavailable data={data} onLeave={leave} /> : (
        <form className="shrink-0 px-4 pt-3 pb-4 border-t" onSubmit={(e) => { e.preventDefault(); ask(a.draft); }}>
          <div className="border border-gr-control-border rounded-[8px] bg-card flex flex-col focus-within:outline focus-within:outline-2 focus-within:-outline-offset-1 focus-within:outline-ring">
            {a.files.map((f) => <FileChip key={f.id} file={f} onRemove={() => a.setFiles((was) => was.filter((x) => x.id !== f.id))} />)}
            <label htmlFor="assistant-ask" className="sr-only">Message the assistant</label>
            <textarea id="assistant-ask" ref={field} rows={2} maxLength={4000} value={a.draft}
              placeholder="Ask about your work, money or reliability" data-testid="assistant-input"
              onChange={(e) => a.setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); ask(a.draft); } }}
              className="border-0 bg-transparent resize-none px-3 pt-2.5 pb-0.5 outline-none placeholder:text-muted-foreground" />
            <div className="flex items-center gap-1.5 px-1.5 pt-1 pb-1.5">
              <input ref={picker} type="file" className="hidden" multiple accept=".pdf,.csv,image/png,image/jpeg,image/webp,image/gif"
                onChange={(e) => { pick(e.target.files || []); e.target.value = ""; }} data-testid="assistant-file-input" />
              <Button type="button" variant="ghost" size="icon" aria-label="Import a file: PDF, CSV or image" title="Import a file: PDF, CSV or image"
                disabled={importing.isPending || a.files.length >= 5} onClick={() => picker.current?.click()} data-testid="assistant-import-btn">
                {importing.isPending ? <LoaderCircle className="motion-safe:animate-spin" /> : <Paperclip />}
              </Button>
              <span className="flex-1 inline-flex items-center gap-1 text-xs text-muted-foreground" data-testid="assistant-web-note">
                {data?.web_search ? <><Globe className="w-3 h-3" aria-hidden="true" />Web search on</>
                  : <><GlobeOff className="w-3 h-3" />Web search off</>}
              </span>
              <Button type="submit" size="icon" aria-label="Send" disabled={!a.draft.trim() || send.isPending || !data || blocked}
                title={blocked ? (reading.length ? "Wait until the file is read." : "Remove the file that couldn't be read.") : undefined}
                data-testid="assistant-send"><ArrowUp /></Button>
            </div>
          </div>
          {a.files.length > 0 && (
            <span className="block mt-2 text-xs text-muted-foreground">Imported files are kept 90 days and seen only by founders</span>
          )}
        </form>
      )}
    </aside>
  );
}

function Bubble({ text, files }) {
  return (
    <div className="self-end max-w-[85%] bg-gr-surface-2 rounded-[10px] px-3 py-2 flex flex-col gap-1" data-testid="assistant-founder">
      <span className="whitespace-pre-wrap break-words">{text}</span>
      {(files || []).map((f) => (
        <span key={f.id} className="flex items-center gap-1.5 text-sm text-muted-foreground min-w-0">
          <FileText className="w-3.5 h-3.5 shrink-0" aria-hidden="true" /><span className="truncate">{f.name}</span>
        </span>
      ))}
    </div>
  );
}

// an imported file in the message box: reading it (with how far), read, or what went wrong; Remove takes it off
// this message (the file itself is kept 90 days, and Delete conversations deletes it)
function FileChip({ file, onRemove }) {
  const reading = file.status === "reading";
  return (
    <div className="mx-2 mt-2 px-2.5 py-2 rounded-[6px] bg-gr-surface-2 flex flex-col gap-1.5" data-testid="assistant-import">
      <span className="flex items-center gap-2 min-w-0">
        <FileText className="w-4 h-4 shrink-0" aria-hidden="true" />
        <span className="flex-1 min-w-0 font-semibold truncate">{file.name}</span>
        <button type="button" aria-label={`Remove ${file.name}`} onClick={onRemove}
          className="w-6 h-6 grid place-items-center rounded-md hover:bg-gr-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">
          <X className="w-3.5 h-3.5" aria-hidden="true" />
        </button>
      </span>
      {reading && (
        <>
          <span role="progressbar" aria-label={`Reading ${file.name}`} aria-valuemin={0} aria-valuemax={file.pages} aria-valuenow={file.read}
            className="block h-[3px] rounded-sm bg-gr-hairline overflow-hidden">
            <span className="block h-full bg-gr-ink-secondary transition-[width]" style={{ width: `${Math.max(4, (100 * file.read) / (file.pages || 1))}%` }} />
          </span>
          <span className="text-xs text-muted-foreground" aria-live="polite">{readingLine(file)}</span>
        </>
      )}
      {file.status === "failed" && <span className="text-xs text-gr-danger" role="alert">{file.problem}</span>}
    </div>
  );
}

// the drawer, for the company's founders while documents are open (FEATURE_DOCUMENTS)
export default function Assistant() {
  const shown = useAssistantShown();
  const { open } = useAssistant();
  if (!open || !shown) return null;
  return <Drawer />;
}
